import { useEffect, useLayoutEffect, useRef, useState } from "react";

/*
  Auto-advancing walkthrough carousel.

  Tracks the outgoing slide separately so the leave animation can play
  while the next one enters, mirroring the is-active/is-leaving pair in
  the stylesheet.
*/

const WALKTHROUGH_INTERVAL = 6500;
const TRANSITION_DURATION = 750;

export default function useCodesWalkthrough(stepCount, enabled) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [leavingIndex, setLeavingIndex] = useState(null);
  const slideRefs = useRef([]);
  const leaveTimerRef = useRef(null);

  useEffect(() => {
    if (!enabled || stepCount < 2) return undefined;

    setActiveIndex(0);
    setLeavingIndex(null);

    const interval = window.setInterval(() => {
      setActiveIndex((current) => {
        setLeavingIndex(current);

        if (leaveTimerRef.current) window.clearTimeout(leaveTimerRef.current);

        leaveTimerRef.current = window.setTimeout(() => {
          setLeavingIndex(null);
          leaveTimerRef.current = null;
        }, TRANSITION_DURATION);

        return (current + 1) % stepCount;
      });
    }, WALKTHROUGH_INTERVAL);

    return () => {
      window.clearInterval(interval);
      if (leaveTimerRef.current) window.clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
      setLeavingIndex(null);
    };
  }, [stepCount, enabled]);

  /*
    React reuses the slide node, so re-applying is-active alone would
    not replay its CSS animation. Force a reflow to restart it.
  */
  useLayoutEffect(() => {
    const node = slideRefs.current[activeIndex];
    if (!node) return;
    // eslint-disable-next-line no-unused-expressions
    node.offsetWidth;
  }, [activeIndex]);

  const registerSlide = (index) => (node) => {
    slideRefs.current[index] = node;
  };

  return { activeIndex, leavingIndex, registerSlide };
}
